"use client";

import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import { useEffect, useRef, useState } from "react";
import { useApi } from "@/hooks/useApi";
import { SessionSkeleton } from "@/components/codesmith/SessionSkeleton";
import { useActiveGithubOrgForUser } from "@/hooks/useConnectedGithubOrgs";
import {
  addCodesmithExtensionEmbed,
  isCodesmithExtensionEmbed,
} from "@/lib/codesmithExtensionEmbed";
import { applyCodesmithExtensionContext } from "@/lib/codesmithExtensionContext";
import { useLatestRef } from "@/hooks/useLatestRef";

export default function CodesmithPrPage() {
  const router = useRouter();
  const api = useApi();
  const apiRef = useLatestRef(api);
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Use the suspense variant so `codesmithEnabled` reflects loaded installation
  // data. The non-suspense variant defaults to false while orgs are still
  // loading, which would prematurely redirect codesmith-enabled orgs on a cold
  // load before installation data arrives.
  const { codesmithEnabled } = useActiveGithubOrgForUser();

  const hasPrRouteParams = Boolean(
    router.query.org && router.query.repo && router.query.prNumber,
  );
  const extensionEmbed = isCodesmithExtensionEmbed(router.query.embed);
  // Embedded extension pages have nowhere to redirect to, so a disabled org
  // renders the error in place.
  const codesmithDisabledInEmbed =
    router.isReady && hasPrRouteParams && !codesmithEnabled && extensionEmbed;
  const error =
    router.isReady && !hasPrRouteParams
      ? "Invalid PR URL format. Expected: /{org}/codesmith/{repo}/pr/{prNumber}"
      : codesmithDisabledInEmbed
      ? "[code]smith is not enabled for this organization."
      : resolveError;

  useEffect(() => {
    if (!router.isReady) return;

    const {
      org,
      repo,
      prNumber,
      prompt,
      autoSend,
      ref: refParam,
      autoLogin,
      migration_autofix: migrationAutofix,
    } = router.query;

    if (!org || !repo || !prNumber) return;

    // Orgs without codesmith access landing on a codesmith route should be
    // redirected to the run history overview instead of attempting to resolve
    // a session they cannot view.
    if (!codesmithEnabled) {
      if (!extensionEmbed) router.replace(`/${org}/runs/jobs`);
      return;
    }

    const repository = `${org}/${repo}`;

    let cancelled = false;

    apiRef.current
      .get("/assistant/resolve-pr", {
        params: { repository, pr_number: prNumber },
      })
      .then((response) => {
        if (cancelled) return;

        const forwardParams = new URLSearchParams();
        if (prompt) forwardParams.set("prompt", String(prompt));
        if (autoSend) forwardParams.set("autoSend", String(autoSend));
        if (refParam) forwardParams.set("ref", String(refParam));
        if (autoLogin) forwardParams.set("autoLogin", String(autoLogin));
        if (migrationAutofix) {
          forwardParams.set("migration_autofix", String(migrationAutofix));
        }

        if (response.data.session_id) {
          // PR already has a session, redirect there
          const basePath = `/${org}/sessions/${response.data.session_id}`;
          const qs = forwardParams.toString();
          const path = qs ? `${basePath}?${qs}` : basePath;
          router.replace(
            extensionEmbed ? addCodesmithExtensionEmbed(path) : path,
          );
        } else {
          // No session yet. Pass the PR metadata from the GitHub API
          // response so the new-session view can show a preview card
          // without any additional fetch.
          forwardParams.set("repo", repository);
          forwardParams.set("pr", String(prNumber));
          // A Linear review's issue rides along as context for the PR's first
          // session; an existing session above already has its own.
          applyCodesmithExtensionContext(forwardParams, router.query);
          if (response.data.pr) {
            forwardParams.set("pr_preview", JSON.stringify(response.data.pr));
          }
          const path = `/${org}/sessions/new?${forwardParams.toString()}`;
          router.replace(
            extensionEmbed ? addCodesmithExtensionEmbed(path) : path,
          );
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message =
          err?.response?.data?.message || "Failed to resolve PR session";
        setResolveError(message);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query, codesmithEnabled, extensionEmbed]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-neutral-500">{error}</p>
        </div>
      </div>
    );
  }

  return <SessionSkeleton />;
}
